import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { puzzleApi } from '../utils/api'

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '32px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  title: {
    margin: 0,
  },
  backLink: {
    padding: '8px 16px',
    border: '2px solid var(--color-border)',
    fontWeight: '600',
    textDecoration: 'none',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    border: '2px solid var(--color-border)',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    background: 'var(--color-light)',
    borderBottom: '2px solid var(--color-border)',
    fontWeight: '700',
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid #ddd',
  },
  rank: {
    fontWeight: '700',
    width: '60px',
  },
  name: {
    fontWeight: '600',
  },
  time: {
    fontFamily: 'var(--font-mono)',
    fontWeight: '600',
  },
  hints: {
    color: 'var(--color-muted)',
  },
  empty: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--color-muted)',
    border: '2px dashed var(--color-border)',
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--color-muted)',
  },
  gold: { color: '#FFD700' },
  silver: { color: '#C0C0C0' },
  bronze: { color: '#CD7F32' },
}

function formatTime(seconds) {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function getRankEmoji(rank) {
  if (rank === 1) return '🥇'
  if (rank === 2) return '🥈'
  if (rank === 3) return '🥉'
  return rank
}

export default function Leaderboard() {
  const { id } = useParams()
  const [puzzle, setPuzzle] = useState(null)
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      try {
        const [puzzleData, leaderboardData] = await Promise.all([
          puzzleApi.get(id),
          puzzleApi.getLeaderboard(id),
        ])
        setPuzzle(puzzleData)
        setEntries(leaderboardData.entries || [])
      } catch (err) {
        console.error('Failed to fetch leaderboard:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id])

  if (loading) {
    return <div style={styles.loading}>Loading leaderboard...</div>
  }

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Leaderboard</h1>
          {puzzle && (
            <p style={{ color: 'var(--color-muted)', marginTop: '4px' }}>
              {puzzle.theme || 'Crossword Puzzle'}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Link to={`/puzzle/${id}`} style={styles.backLink}>
            ← Solve Puzzle
          </Link>
        </div>
      </div>

      {entries.length === 0 ? (
        <div style={styles.empty}>
          <p>No one has completed this puzzle yet.</p>
          <p>Be the first!</p>
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Rank</th>
              <th style={styles.th}>Solver</th>
              <th style={styles.th}>Time</th>
              <th style={styles.th}>Hints</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, idx) => (
              <tr key={entry.id}>
                <td style={{ ...styles.td, ...styles.rank }}>
                  {getRankEmoji(idx + 1)}
                </td>
                <td style={{ ...styles.td, ...styles.name }}>
                  {entry.solverName}
                </td>
                <td style={{ ...styles.td, ...styles.time }}>
                  {formatTime(entry.solveTime)}
                </td>
                <td style={{ ...styles.td, ...styles.hints }}>
                  {entry.hintsUsed > 0 ? `${entry.hintsUsed} used` : 'None'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
